import { Spin, StepProps, Button, Col, Steps, Tag, Row, Divider, Card } from 'antd';
import React, { useState, useEffect, ReactNode, useRef, useContext } from 'react';
import { CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Connect from '../Connect';
import styles from './styles.module.css';
import TryOut from '../TryOut';
import { HitchConfigurationHook } from 'components/public/integrations/useHitchConfiguration';
import GoLive from '../GoLive';
import QuickLook from '../QuickLook';
import capitalize from 'components/utils/capitalize';
import { useRouter } from 'next/router';
import { GlobalStateContext } from 'components/context/GlobalStateProvider';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { parseHitchId } from 'components/SubscribeForm/utils';

const { Step } = Steps;

const TagText = ({ text }: { text: string }) => <span style={{ paddingLeft: 3 }}>{text}</span>;

const OnboardHitch = ({
  hitchConfigurationHook,
}: {
  hitchConfigurationHook: HitchConfigurationHook;
}): JSX.Element => {
  const {
    hitchId,
    hitchConfiguration,
    hitchGoLive,
    useCaseConfiguration,
    connectConfiguration,
    loading,
  } = hitchConfigurationHook;

  const { setCurrentPage, currentPage } = useCaseConfiguration;

  // workaround with 'global' app (nextjs) context to catch flickering state when callback after authentication happens
  // to be able to come to the right "step" in the process
  const { step, setStep } = useContext(GlobalStateContext);
  const [hasReset, setHasReset] = useState<boolean>(false);
  const { query, asPath } = useRouter();
  const { formatMessage } = useIntl();
  const [inTransition, setInTransition] = useState<boolean>();

  const onChangeCurrent = (nextCurrent: number) => {
    setCurrentPage(nextCurrent);
  };

  // workaround with 'global' app (nextjs) context to catch flickering state when callback after authentication happens
  // to be able to come to the right "step" in the process
  useEffect(() => {
    // when callback from authorising an app, this makes sure we come back to the same step
    if (query.step === 'connect') {
      setStep('connect');
    }
  }, [query.step, hasReset, setHasReset, asPath, setStep, step]);

  useEffect(() => {
    // First page should not scroll down immediately
    if (currentPage !== 0) {
      stepRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setInTransition(false);
  }, [currentPage]);

  // workaround with 'global' app (nextjs) context to catch flickering state when callback after authentication happens
  // to be able to come to the right "step" in the process
  useEffect(() => {
    if (step === 'connect') {
      setCurrentPage(1);
    }
  }, [step, setCurrentPage]);

  let nextTextAfterUsecase;

  if (useCaseConfiguration.errorMessage) {
    nextTextAfterUsecase = <p>{useCaseConfiguration.errorMessage}</p>;
  } else if (!query.hitchRef) {
    nextTextAfterUsecase = (
      <Col span={24} style={{ marginTop: 24 }}>
        <h2>
          {formatMessage({
            defaultMessage: `Want to give it a try?`,
            description: 'onboarding/tryout/teaser/title',
          })}
        </h2>
        <p>
          {formatMessage({
            defaultMessage: `Good news! We offer one month for free, so you can test if this works out for you.`,
            description: 'onboarding/tryout/teaser/description/1',
          })}
        </p>
        <p>
          {formatMessage({
            defaultMessage: `No worries, you can delete everything at any time.`,
            description: 'onboarding/tryout/teaser/description/2',
          })}
        </p>
        <p>
          {formatMessage({
            defaultMessage: `And if you are happy, which you will 😊, at the end of the trail period we will ask you for your payment details.`,
            description: 'onboarding/tryout/teaser/description/2',
          })}
        </p>
      </Col>
    );
  }

  const steps: (StepProps & {
    content: ReactNode;
    title: string;
    nextText?: JSX.Element;
    isFulfilled: () => boolean;
    nextFn?: () => Promise<void>;
    nextButtonText?: string;
  })[] = [
    {
      title: formatMessage({
        defaultMessage: 'What do you want?',
        description: 'onboarding/step/usecase/title',
      }),
      content: <QuickLook useCaseConfiguration={useCaseConfiguration} />,
      description: (
        <Tag
          color={useCaseConfiguration.selectedUseCase?.title ? 'green' : 'red'}
          closable={!!useCaseConfiguration.selectedUseCase?.title}
          onClose={() => useCaseConfiguration.setSelectedUseCase()}
          visible
        >
          {useCaseConfiguration.selectedUseCase?.title ? <CheckOutlined /> : <InfoCircleOutlined />}
          <TagText
            text={
              useCaseConfiguration.selectedUseCase?.title
                ? formatMessage(useCaseConfiguration.selectedUseCase.title)
                : formatMessage({
                    defaultMessage: 'Select a use case',
                    description: 'onboarding/usecase/select',
                  })
            }
          />
        </Tag>
      ),
      nextText: nextTextAfterUsecase,
      nextButtonText: formatMessage({
        defaultMessage: `Connect your apps`,
        description: 'onboarding/connect/teaser/title',
      }),
      isFulfilled: () =>
        !!useCaseConfiguration.selectedUseCase && !useCaseConfiguration.errorMessage,
    },
    {
      title: formatMessage({
        defaultMessage: 'Connect',
        description: 'onboarding/step/connect/title',
      }),
      content: <Connect hitchId={hitchId} connectConfiguration={connectConfiguration} />,
      description: !connectConfiguration.hitch.loading ? (
        <Row gutter={[8, 8]}>
          {parseHitchId(hitchId).map((appName) => {
            return (
              <Col key={appName}>
                <Tag
                  visible
                  color={connectConfiguration?.hitch?.isConnected(appName) ? 'green' : 'red'}
                >
                  {connectConfiguration?.hitch?.isConnected(appName) ? (
                    <CheckOutlined />
                  ) : (
                    <InfoCircleOutlined />
                  )}
                  <TagText text={capitalize(appName)} />
                </Tag>
              </Col>
            );
          })}
        </Row>
      ) : undefined,
      nextText: <p>Connected to both? Click next to configure the integration</p>,
      isFulfilled: () => connectConfiguration.hitch?.isConnected(),
      nextFn: hitchConfiguration.createHike,
      nextButtonText: formatMessage({
        defaultMessage: `Configure`,
        description: 'onboarding/preferences/teaser/title',
      }),
    },
    {
      title: formatMessage({
        defaultMessage: 'Preview',
        description: '/onboarding/step/preview/title',
      }),
      description: formatMessage({
        defaultMessage: 'Choose your preferences',
        description: '/onboarding/nextstep/preview/description',
      }),
      content: <TryOut hitchConfiguration={hitchConfiguration} loading={loading} />,
      nextText: hitchConfiguration.isFulfilled ? (
        <>
          <p>
            {formatMessage({
              defaultMessage: `Satisfied for now? Click next to go live.`,
              description: '/onboarding/golive/teaser/1',
            })}
          </p>
        </>
      ) : (
        <p className={styles.error}>
          You have unsaved changes above, first save them if you are satified
        </p>
      ),
      isFulfilled: () => hitchConfiguration.isFulfilled,
      nextButtonText: formatMessage({
        defaultMessage: `Choose billing plan`,
        description: 'onboarding/gobilling/teaser/title',
      }),
    },
    {
      title: formatMessage({
        defaultMessage: 'Alive and kicking',
        description: '/onboarding/nextstep/button/live',
      }),
      description: formatMessage({
        defaultMessage: 'Activate your free trail',
        description: '/onboarding/nextstep/live/description',
      }),
      content: <GoLive hike={hitchConfiguration.currentHike} hitchGoLive={hitchGoLive} />,
      isFulfilled: () => hitchConfiguration.isFulfilled,
      nextButtonText: formatMessage({
        defaultMessage: `Go live`,
        description: 'onboarding/golive/teaser/title',
      }),
      nextFn: hitchGoLive.goLive,
    },
  ];

  useEffect(() => {
    if (currentPage > 0 && !useCaseConfiguration.selectedUseCase) {
      setCurrentPage(0);
    }
  }, [currentPage, setCurrentPage, useCaseConfiguration.selectedUseCase]);

  useEffect(() => {
    if (currentPage > 1 && !connectConfiguration.hitch.isConnected()) {
      setCurrentPage(1);
    }
  }, [connectConfiguration]);

  const stepRef = useRef<HTMLDivElement>(null);

  const next = async (fn?: () => Promise<void>) => {
    setInTransition(true);
    if (fn) {
      await fn();
    }
    const newPage = currentPage + 1;

    if (newPage > steps.length - 1) {
      // last page ; dont change current
      return;
    }

    if (newPage === 1 && connectConfiguration.hitch.isConnected()) {
      onChangeCurrent(newPage + 1);
      return;
    }
    onChangeCurrent(newPage);
  };

  const prev = () => {
    onChangeCurrent(currentPage - 1);
  };

  const stepper = steps.map((item, index) => (
    <Step
      key={item.title}
      title={item.title}
      {...(index > currentPage ? { icon: item.icon } : {})}
      description={item.description}
    />
  ));

  return (
    <>
      <Col span={24}>
        <Row justify="center">
          <Col ref={stepRef} className={classnames('scroll-margin-top')}>
            <Card bordered>
              <h2>
                {formatMessage({
                  defaultMessage: 'Setup instructions',
                  description: '/onboarding/setup/title',
                })}
              </h2>
              <h3>
                {formatMessage({
                  defaultMessage: 'Complete the following steps to activate the integration',
                  description: '/onboarding/setup/description',
                })}
              </h3>
              <Steps
                direction="vertical"
                className={classnames(styles.stepper)}
                current={currentPage}
              >
                {stepper}
              </Steps>
            </Card>
          </Col>
        </Row>
      </Col>
      <Divider />
      <Col span={24}>
        <div className={styles.stepsContent}>{steps[currentPage].content}</div>
      </Col>
      {steps[currentPage] && steps[currentPage].nextText && (
        <Col span={24}>{steps[currentPage].nextText}</Col>
      )}
      <Col span={24} className={styles.stepsAction}>
        <Row justify="center" gutter={[32, 32]}>
          {currentPage > 0 && (
            <Col>
              <Spin spinning={inTransition}>
                <Button disabled={inTransition} onClick={() => prev()}>
                  {formatMessage({
                    defaultMessage: 'Previous',
                    description: '/onboarding/nextstep/button/previous',
                  })}
                </Button>
              </Spin>
            </Col>
          )}
          <Col>
            <Spin spinning={inTransition}>
              <Button
                disabled={inTransition || !steps[currentPage].isFulfilled()}
                type="primary"
                onClick={() => next(steps[currentPage].nextFn)}
              >
                {steps[currentPage].nextButtonText ||
                  formatMessage({
                    defaultMessage: 'Next',
                    description: '/onboarding/nextstep/button/next',
                  })}
              </Button>
            </Spin>
          </Col>
        </Row>
      </Col>
    </>
  );
};
export default OnboardHitch;
